/**
 * Bot v1: deterministic, no ML, no network.
 *
 * The bot understands draws, discards, chow / pong / kong claims, winning on a
 * discard and winning on a self-draw. It has no privileged path into the
 * engine: every move it makes comes from `getLegalActions`, and it is a pure
 * function of the state, so the same position always produces the same move.
 */

import {
  getConcealedTiles,
  getLegalActions,
  getPlayer,
  getRuleSet,
  isGameOver,
} from "@kikik/mahjong-core";
import type { Action, ClaimAction, GameState, PlayerId, Tile, TileKind } from "@kikik/mahjong-core";
import { rankTilesForDiscard } from "./heuristics.js";
import type { DiscardCandidate } from "./heuristics.js";
import { shanten } from "./shanten.js";

export interface DiscardPlan {
  candidate: DiscardCandidate;
  /** Shanten of the hand that remains after this discard. */
  shantenAfter: number;
}

export interface ClaimPlan {
  action: ClaimAction;
  shantenBefore: number;
  shantenAfter: number;
  accept: boolean;
}

function kindsOf(tiles: readonly Tile[]): TileKind[] {
  return tiles.map((tile) => tile.kind);
}

function without(tiles: readonly Tile[], tileIds: readonly number[]): Tile[] {
  const removing = new Set(tileIds);
  return tiles.filter((tile) => !removing.has(tile.id));
}

/**
 * Rank every legal discard: fewest shanten first, then the tile the hand wants
 * least (lone honors and terminals before pairs and runs). Fully deterministic.
 */
export function planDiscards(state: GameState, playerId: PlayerId): DiscardPlan[] {
  const player = getPlayer(state, playerId);
  const concealed = getConcealedTiles(player);
  const legalIds = new Set(
    getLegalActions(state, playerId).flatMap((action) =>
      action.type === "discard" ? [action.tileId] : [],
    ),
  );

  return rankTilesForDiscard(concealed)
    .filter((candidate) => legalIds.has(candidate.tile.id))
    .map((candidate) => ({
      candidate,
      shantenAfter: shanten(
        kindsOf(without(concealed, [candidate.tile.id])),
        player.melds.length,
      ),
    }))
    .sort((a, b) => {
      if (a.shantenAfter !== b.shantenAfter) return a.shantenAfter - b.shantenAfter;
      // rankTilesForDiscard already ordered ties deterministically.
      return 0;
    });
}

/**
 * Should this claim be taken? A chow or pong must actually bring the hand
 * closer to winning; a kong only has to avoid making it worse.
 */
export function planClaim(state: GameState, action: ClaimAction): ClaimPlan {
  const player = getPlayer(state, action.playerId);
  const melds = player.melds.length;
  const before = shanten(kindsOf(player.hand), melds);

  if (action.type === "ron") {
    return { action, shantenBefore: before, shantenAfter: -1, accept: true };
  }

  const after = shanten(kindsOf(without(player.hand, action.tileIds)), melds + 1);
  const accept = action.type === "kong" ? after <= before : after < before;
  return { action, shantenBefore: before, shantenAfter: after, accept };
}

function bestClaim(state: GameState, playerId: PlayerId): Action | null {
  const legal = getLegalActions(state, playerId);
  const ruleSet = getRuleSet(state.ruleSetId);

  const ron = legal.find((action) => action.type === "ron");
  if (ron) return ron;

  const plans = legal
    .filter((action): action is ClaimAction =>
      action.type === "chow" || action.type === "pong" || action.type === "kong",
    )
    .map((action) => planClaim(state, action))
    .filter((plan) => plan.accept)
    .sort((a, b) => {
      if (a.shantenAfter !== b.shantenAfter) return a.shantenAfter - b.shantenAfter;
      const byPriority =
        ruleSet.claimPriority.indexOf(a.action.type) - ruleSet.claimPriority.indexOf(b.action.type);
      if (byPriority !== 0) return byPriority;
      return (a.action.tileIds[0] ?? 0) - (b.action.tileIds[0] ?? 0);
    });

  const best = plans[0];
  if (best) return best.action;
  return legal.find((action) => action.type === "pass") ?? null;
}

/**
 * Decide one action. Returns null when the bot has nothing legal to do
 * (for example when it is not this player's turn).
 */
export function decideBotAction(state: GameState, playerId: PlayerId): Action | null {
  if (isGameOver(state)) return null;
  const legal = getLegalActions(state, playerId);
  if (legal.length === 0) return null;

  if (state.phase === "awaiting-claims") return bestClaim(state, playerId);

  const win = legal.find((action) => action.type === "declare-win");
  if (win) return win;

  const draw = legal.find((action) => action.type === "draw");
  if (draw) return draw;

  // A kong from our own hand, when it does not push the hand backwards.
  const kongs = legal
    .filter((action): action is ClaimAction => action.type === "kong")
    .map((action) => planClaim(state, action))
    .filter((plan) => plan.accept);
  const kong = kongs[0];
  if (kong) return kong.action;

  const plan = planDiscards(state, playerId)[0];
  if (plan) {
    return { type: "discard", playerId, tileId: plan.candidate.tile.id };
  }
  return legal[0] ?? null;
}

/** The player the bot is waiting on, if any: the seat that must act next. */
export function nextActor(state: GameState): PlayerId | null {
  if (isGameOver(state)) return null;
  if (state.phase === "awaiting-claims") {
    return state.pendingClaims?.waitingOn[0] ?? null;
  }
  return state.turn;
}
